import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from '../utils/safeToast';
import { LogIn, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = "/api";

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [adminExists, setAdminExists] = useState(true); // Default: verstecken
  const [updateMessage, setUpdateMessage] = useState(null);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Bei bereits eingeloggtem User: Redirect zur Return-URL oder Dashboard
  // WICHTIG: Muss den return Parameter aus URL berücksichtigen (für Android QR Flow)
  useEffect(() => {
    if (user) {
      // Prüfe ob return Parameter in URL (Android QR Staff Flow)
      const urlParams = new URLSearchParams(window.location.search);
      const returnUrl = urlParams.get('return');
      
      if (returnUrl && (returnUrl.startsWith('/scan/') || 
                        returnUrl.startsWith('/guest/') || 
                        returnUrl.startsWith('/departure/'))) {
        // Öffentliche Seiten müssen mit window.location.href navigiert werden
        window.location.href = returnUrl;
      } else if (returnUrl) {
        navigate(returnUrl);
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  // Prüfe URL-Parameter für Update/Session-Meldungen
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');
    
    if (reason === 'updated') {
      setUpdateMessage('Die App wurde aktualisiert. Bitte melden Sie sich erneut an.');
      // URL bereinigen
      window.history.replaceState({}, '', '/login');
    } else if (reason === 'session_expired') {
      setUpdateMessage('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  // Prüfen, ob bereits ein Admin existiert
  useEffect(() => {
    const checkAdminExists = async () => {
      try {
        const response = await axios.get(`${API}/auth/admin-exists`);
        setAdminExists(response.data.admin_exists);
      } catch (error) {
        // Bei Fehler Button verstecken (Sicherheit)
        setAdminExists(true);
      }
    };
    checkAdminExists();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // DEBUG LOGGING für Production
    const debugLog = (msg, data = null) => {
      const logData = data ? JSON.stringify(data, null, 2) : '';
      console.log(`[AUTH DEBUG] ${msg}`, logData);
    };
    
    try {
      debugLog('Login attempt', { username, apiUrl: API });
      
      await login(username, password);
      
      debugLog('Login successful, checking token storage');
      
      // Check token storage
      let tokenStored = false;
      try {
        tokenStored = !!localStorage.getItem('token');
      } catch (e) {
        debugLog('localStorage check failed', { error: e.message });
      }
      debugLog('Token stored', { tokenStored });
      
      // FIX: Primär URL-Parameter für Return-URL (Android-kompatibel)
      // sessionStorage ist Fallback für Legacy-Aufrufe
      let returnUrl = null;
      
      // 1. Priorität: URL-Parameter (Android-kompatibel)
      const urlParams = new URLSearchParams(window.location.search);
      returnUrl = urlParams.get('return');
      debugLog('URL return param', { returnUrl });
      
      // 2. Fallback: sessionStorage (Legacy, funktioniert auf Desktop)
      if (!returnUrl) {
        try {
          returnUrl = sessionStorage.getItem('returnAfterLogin');
          if (returnUrl) {
            sessionStorage.removeItem('returnAfterLogin');
            debugLog('sessionStorage return', { returnUrl });
          }
        } catch (e) {
          console.warn('sessionStorage not available:', e);
        }
      }
      
      const targetUrl = returnUrl || '/dashboard';
      debugLog('Redirect target', { returnUrl, targetUrl });
      
      // Für öffentliche QR-Seiten (/scan/, /guest/, /departure/) 
      // MUSS window.location.href verwendet werden, da diese außerhalb 
      // des React-Router-Kontexts liegen
      if (targetUrl.startsWith('/scan/') || 
          targetUrl.startsWith('/guest/') || 
          targetUrl.startsWith('/departure/')) {
        debugLog('Using window.location.href for public page');
        window.location.href = targetUrl;
      } else {
        debugLog('Using navigate for protected route');
        navigate(targetUrl);
      }
      
    } catch (error) {
      // DEBUG: Log full error details
      debugLog('Login FAILED', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        detail: error?.response?.data?.detail,
        message: error?.message,
        url: error?.config?.url
      });
      
      // Fehler-Toast ist sicher, da wir auf der Login-Seite bleiben
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleInitAdmin = async () => {
    setInitLoading(true);
    try {
      const response = await axios.post(`${API}/init-admin`);
      if (response.data.username) {
        toast.success(`Admin erstellt: ${response.data.username} / ${response.data.password}`);
        setUsername(response.data.username);
        setPassword(response.data.password);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen des Admin-Kontos');
    } finally {
      setInitLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1651001291307-ddf021d44a10?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-[#0E4C92]/80" />
      </div>

      {/* Login Card */}
      <Card className="relative w-full max-w-md bg-white shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-[#0E4C92] rounded-2xl flex items-center justify-center mb-4">
            <span className="text-white font-bold text-2xl font-['Manrope']">GB</span>
          </div>
          <CardTitle className="text-2xl font-['Manrope'] text-slate-900">
            GB Objekt- & Aufgabenmanagement
          </CardTitle>
          <CardDescription className="text-slate-500">
            Sylt Ferienwohnungen
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Update/Session Meldung */}
          {updateMessage && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700 text-center">{updateMessage}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-700">Benutzername</Label>
              <Input
                id="username"
                type="text"
                placeholder="IhrBenutzername"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="bg-slate-50 border-slate-200 focus:border-[#0E4C92] focus:ring-[#0E4C92]"
                data-testid="login-username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Passwort</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-slate-50 border-slate-200 focus:border-[#0E4C92] focus:ring-[#0E4C92]"
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#0E4C92] hover:bg-[#0A3A70] text-white rounded-lg py-6"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <LogIn className="w-5 h-5 mr-2" />
              )}
              Anmelden
            </Button>
          </form>

          {/* Admin initialisieren - nur anzeigen wenn noch kein Admin existiert */}
          {!adminExists && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-500 text-center mb-3">
                Erstes Mal hier? Admin-Konto erstellen:
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-lg border-slate-300"
                onClick={handleInitAdmin}
                disabled={initLoading}
                data-testid="init-admin-btn"
              >
                {initLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Admin initialisieren
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
